/**
 * Le fil : ce qui s'est passé juste avant, pour lire un rapport de panne.
 *
 * # Le problème qu'il résout
 *
 * Un `app_error` dit qu'une exception a eu lieu, où, et la tête de sa pile.
 * Il ne dit pas ce qu'il y avait *autour*, et sur les pannes qui comptent
 * c'est l'autour qui explique. Le cas fondateur : sur iPad, après deux heures
 * d'écran verrouillé, le décompte du passage en rythme ne repart pas. Le
 * rapport disait « bloqué » ; ce qu'il fallait lire, c'est « page cachée à
 * 14 h 02, contexte audio interrompu dans la seconde, page visible à 16 h 10,
 * contexte toujours interrompu au moment du geste ». Sans ces quatre lignes,
 * le seul outil était un iPad branché à un Mac, et la panne ne se reproduisait
 * pas sous l'inspecteur.
 *
 * # Ce qu'il retient, et rien d'autre
 *
 * Deux sources, en vocabulaire fermé : la visibilité de la page et l'état du
 * contexte audio. Ni frappe, ni chemin, ni contenu — chaque entrée est un mot
 * pris dans une liste et un âge. Le fil se joint à `app_error` et à
 * `run_stalled`, et à eux seuls : c'est un complément de rapport de panne, pas
 * un journal d'usage.
 *
 * Borné en nombre, et sérialisé en une chaîne courte plutôt qu'en tableau :
 * une ligne se lit d'un coup d'œil dans PostHog, et son coût en octets est
 * connu d'avance sur un payload que l'API plafonne à quatre kilo-octets.
 *
 * # Pourquoi les âges et non les heures
 *
 * Une heure absolue demande l'horloge du poste, qui ment souvent ; un âge
 * relatif à l'instant du rapport se lit sans rien savoir du poste : « il y a
 * 7 212 s » suffit pour reconnaître une veille de deux heures.
 */

export type TrailSource = "page" | "audio";

export type TrailEntry = {
  readonly source: TrailSource;
  /** Un mot d'un vocabulaire fermé : `visible`, `hidden`, `running`, `interrupted`… */
  readonly value: string;
  /** Instant de l'entrée, en millisecondes d'horloge monotone. */
  readonly at: number;
};

/** Entrées gardées. Au-delà, les plus anciennes s'effacent. */
export const TRAIL_LENGTH = 12;

/** Longueur maximale de la chaîne rendue, en caractères. */
const RENDERED_LIMIT = 400;

let entries: TrailEntry[] = [];

function now(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }

  return Date.now();
}

/**
 * Note une transition. Une valeur identique à la précédente de la même source
 * n'ajoute rien : un `statechange` qui confirme l'état n'est pas un fait.
 */
export function recordTrail(source: TrailSource, value: string): void {
  const last = [...entries].reverse().find((entry) => entry.source === source);

  if (last !== undefined && last.value === value) {
    return;
  }

  entries.push({ source, value, at: now() });

  if (entries.length > TRAIL_LENGTH) {
    entries = entries.slice(-TRAIL_LENGTH);
  }
}

/**
 * Le fil, du plus ancien au plus récent, chaque entrée avec son âge en
 * secondes : `page:hidden -7212s > audio:interrupted -7211s > page:visible -3s`.
 *
 * `null` quand rien n'a encore été noté, pour que la propriété s'absente du
 * payload au lieu d'y figurer vide.
 */
export function renderTrail(at: number = now()): string | null {
  if (entries.length === 0) {
    return null;
  }

  const rendered = entries
    .map((entry) => `${entry.source}:${entry.value} -${Math.max(0, Math.round((at - entry.at) / 1000))}s`)
    .join(" > ");

  return rendered.length > RENDERED_LIMIT ? rendered.slice(-RENDERED_LIMIT) : rendered;
}

/** Ce que le fil a en mémoire, pour les tests et le diagnostic. */
export function readTrail(): readonly TrailEntry[] {
  return entries;
}

export function clearTrail(): void {
  entries = [];
}

/**
 * Ce que l'observation de la page demande du navigateur, et rien de plus.
 */
export type VisibilityTarget = Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;

/**
 * Suit la visibilité de la page pour la vie de `target`. Note l'état initial,
 * puis chaque changement. Renvoie de quoi se retirer.
 */
export function observePageVisibility(target: VisibilityTarget): () => void {
  const onChange = () => {
    recordTrail("page", target.visibilityState);
  };

  onChange();
  target.addEventListener("visibilitychange", onChange);

  return () => {
    target.removeEventListener("visibilitychange", onChange);
  };
}

/** Ce que l'observation du son demande d'un `AudioContext`, et rien de plus. */
export type ObservableAudioContext = Pick<AudioContext, "state" | "addEventListener" | "removeEventListener">;

/**
 * Suit l'état du contexte audio pour sa vie. `interrupted`, que Safari pose
 * quand l'iPad verrouille l'écran ou qu'une autre application prend la
 * sortie, ne figure pas dans le type DOM standard : on lit l'état tel qu'il
 * vient, sans le filtrer.
 */
export function observeAudioState(context: ObservableAudioContext): () => void {
  const onChange = () => {
    recordTrail("audio", String(context.state));
  };

  onChange();
  context.addEventListener("statechange", onChange);

  return () => {
    context.removeEventListener("statechange", onChange);
  };
}
